import moment from "moment";
import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2Icon, ClockIcon, HammerIcon, LoaderIcon, Repeat2Icon, UserCheck } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository.ts";
import { usePropertyAppointments } from "../properties-detail.hooks.ts";
import { uuid } from "@/interfaces";
import { appointment_status } from "@/models/appointment.model.ts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";

interface PropertyAppointmentsTableProps {
  propertyId: uuid;
}

export default function PropertyAppointmentsTable({ propertyId }: PropertyAppointmentsTableProps) {
  const {
    appointments,
    loading,
    error,
    total,
    filters,
    filterOptions,
    pagination,
    updateStatus,
    updateServiceParent,
    updateAssignee,
    updateSearch,
    updateSortKey,
    updateSortOrder,
    updatePage,
    updatePageSize,
  } = usePropertyAppointments(propertyId);

  const handleSort = (key: string) => {
    if (filters.sortKey === key) {
      updateSortOrder(filters.sortOrder === "asc" ? "desc" : "asc");
    } else {
      updateSortKey(key);
      updateSortOrder("asc");
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format("MM/DD/YYYY");
  };

  const clearAllFilters = () => {
    updateSearch("");
    updateStatus("");
    updateServiceParent("");
    updateAssignee("");
  };

  // Define table columns
  const columns: ColumnDef<AppointmentJoin>[] = [
    {
      header: "ID",
      accessor: "id",
      sortable: true,
      sortKey: "id",
      className: "w-[100px]",
    },
    {
      header: "Unit",
      accessor: (row) => (
        <div className="flex items-start gap-2 flex-col">
          <span className="font-medium">{row.property.name}</span>
          <span className="font-normal text-muted-foreground">
            {row.property.unit_number && `Unit ${row.property.unit_number}`}
            {row.property.beds > 0 && ` • ${row.property.beds} bed${row.property.beds > 1 ? "s" : ""}`}
            {row.property.baths > 0 && ` • ${row.property.baths} bath${row.property.baths > 1 ? "s" : ""}`}
          </span>
        </div>
      ),
      sortable: true,
      sortKey: "unit_number",
    },
    {
      header: "Assigned To",
      accessor: (row) => row.assigned_user?.name || "Unassigned",
      sortable: true,
      sortKey: "assigned_user",
    },
    {
      header: "Service",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.service.name}</span>
          {row.service.parent_name && <span className="text-xs text-muted-foreground">{row.service.parent_name}</span>}
        </div>
      ),
      sortable: true,
      sortKey: "service",
    },
    {
      header: "Created",
      accessor: (row) => formatDate(row.created_at),
      sortable: true,
      sortKey: "created_at",
    },
    {
      header: "Scheduled",
      accessor: (row) => formatDate(row.date),
      sortable: true,
      sortKey: "date",
    },
    {
      header: "Status",
      accessor: (row) => (
        <Badge
          variant={getStatusBadgeVariant(row.status)}
          className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
        >
          {getStatusIcon(row.status)}
          <span className="capitalize">{row.status.replace("_", " ")}</span>
        </Badge>
      ),
      sortable: true,
      sortKey: "status",
    },
  ];

  // Define filters for the table
  const tableFilters: FilterItem[] = [
    {
      type: "search",
      title: "Search appointments...",
      value: filters.search,
      onValueChange: updateSearch,
      grid: 3,
    },
    {
      type: "select",
      title: "Status",
      value: filters.status || "",
      onValueChange: (value) => updateStatus(value),
      options: [
        ...Object.values(appointment_status).map((status) => ({
          label: status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          value: status,
        })),
      ],
      grid: 2,
    },
    {
      type: "select",
      title: "Service",
      value: filters.serviceParent || "",
      onValueChange: (value) => updateServiceParent(value),
      options: [
        ...filterOptions.serviceParents.map((service) => ({
          label: service,
          value: service,
        })),
      ],
      grid: 2,
    },
    {
      type: "select",
      title: "Assignee",
      value: filters.assignee || "",
      onValueChange: (value) => updateAssignee(value),
      options: [
        ...filterOptions.assigneeNames.map((assignee) => ({
          label: assignee,
          value: assignee,
        })),
      ],
      grid: 2,
    },
  ];

  const getStatusIcon = (status: appointment_status) => {
    switch (status) {
      case appointment_status.pending:
        return <ClockIcon className="text-yellow-500 dark:text-yellow-400" />;
      case appointment_status.assigned:
        return <UserCheck className="text-blue-500 dark:text-blue-400" />;
      case appointment_status.inprogress:
        return <HammerIcon className="text-indigo-500 dark:text-indigo-400" />;
      case appointment_status.done:
      case appointment_status.completed:
        return <CheckCircle2Icon className="text-green-500 dark:text-green-400" />;
      case appointment_status.goback:
        return <Repeat2Icon className="text-red-500 dark:text-red-400" />;
      default:
        return <LoaderIcon />;
    }
  };

  const getStatusBadgeVariant = (status: appointment_status) => {
    switch (status) {
      case appointment_status.pending:
        return "secondary";
      case appointment_status.assigned:
        return "default";
      case appointment_status.inprogress:
        return "secondary";
      case appointment_status.done:
      case appointment_status.completed:
        return "default";
      case appointment_status.goback:
        return "destructive";
      default:
        return "secondary";
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Property Appointments</CardTitle>
          <CardDescription>Loading appointments...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <LoaderIcon className="h-8 w-8 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Property Appointments</CardTitle>
          <CardDescription>Error loading appointments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-center py-4">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Property Appointments</CardTitle>
          <CardDescription>Appointments for this property</CardDescription>
        </CardHeader>
        <CardContent>
          <SimpleTable
            columns={columns}
            data={appointments}
            totalCount={total}
            page={pagination.page}
            pageSize={pagination.pageSize}
            setPage={updatePage}
            setPageSize={updatePageSize}
            sortKey={filters.sortKey}
            sortOrder={filters.sortOrder}
            onSortChange={handleSort}
            filters={tableFilters}
            onReset={clearAllFilters}
            container={false}
          />
        </CardContent>
      </Card>
    </div>
  );
}
